import React from 'react';
import classNames from 'classnames';

import FigureTile, { FigureTileProps } from './FigureTile';

/**
 * What sits between two tiles: an operator that reads the tiles as a sum, a plain vertical divider, or a blank the width
 * of an operator, so a row that is not a sum still lines up with one above it.
 */
export type FigureJoin = 'equals' | 'plus' | 'minus' | 'times' | 'divide' | 'divider' | 'space';

const JOIN_SYMBOLS: Record<Exclude<FigureJoin, 'divider' | 'space'>, string> = {
  equals: '=',
  plus: '+',
  minus: '−',
  times: '×',
  divide: '÷',
};

interface FigureTileSlot {
  key: string;
  /** Opt-in: drawn between the previous tile and this one, e.g. Available = Allocated + Residual. Ignored on the first tile. */
  joinBefore?: FigureJoin;
}

/**
 * An empty cell the width of a tile, so a row with fewer tiles lines its later tiles up with a longer row's, e.g. the
 * Allocation rate under the Utilisation rate. Hidden once the row wraps.
 */
export interface FigureTileBlank extends FigureTileSlot {
  blank: true;
}

export type FigureTileItem = (FigureTileProps & FigureTileSlot & { blank?: never }) | FigureTileBlank;

export interface FigureTileGroupProps {
  title: string;
  /** Muted text after the title, e.g. "Available vs budgeted". */
  subtitle?: string;
  /** Pick to keep the page's heading order. Default 'h3'. */
  titleTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  items: FigureTileItem[];
  dataCy?: string;
}

/** A tile, or an empty cell of the same width for a blank. */
const renderTile = (item: FigureTileItem, groupDataCy: string): React.ReactElement => {
  if (item.blank) {
    return <div className="figure-tile figure-tile-blank" aria-hidden="true" data-cy={`${groupDataCy}-${item.key}`} />;
  }
  const { key, joinBefore: _joinBefore, dataCy, ...tile } = item;
  return <FigureTile dataCy={dataCy ?? `${groupDataCy}-${key}`} {...tile} />;
};

/**
 * A titled, bordered group of figure tiles in one row, each taking an equal share of the width. Tiles can be joined by
 * operators so the row reads as the sum it is; the operators are decorative (aria-hidden), since each tile's
 * description and tooltip already say how it is worked out. Below the large breakpoint the row wraps to two columns
 * and the joins are hidden, because a wrapped sum no longer reads left to right.
 */
const FigureTileGroup: React.FC<FigureTileGroupProps> = ({
  title,
  subtitle,
  titleTag: TitleTag = 'h3',
  items,
  dataCy = 'figure-tile-group',
}) => (
  <section className="figure-tile-group" data-cy={dataCy}>
    <div className="figure-tile-group-header">
      <TitleTag className="figure-tile-group-title">{title}</TitleTag>
      {subtitle && <span className="figure-tile-group-subtitle">{subtitle}</span>}
    </div>
    <div className="figure-tile-row">
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {item.joinBefore && index > 0 && (
            <span
              className={classNames('figure-tile-join', item.joinBefore)}
              aria-hidden="true"
              data-cy={`${dataCy}-join-${item.key}`}
            >
              {item.joinBefore === 'divider' || item.joinBefore === 'space' ? null : JOIN_SYMBOLS[item.joinBefore]}
            </span>
          )}
          {renderTile(item, dataCy)}
        </React.Fragment>
      ))}
    </div>
  </section>
);

export default FigureTileGroup;
